import React, { useState, useEffect } from 'react';
import apiHelper from '../../utils/apiHelpers';
import Loader from '../common/Loader';
import CardsNotFoundList from '../card/CardsNotFoundList';
import CardWishlistItem from './CardWishlistItem';
import cardUtils from '../../utils/cardUtils';
import CardWishlistList from './CardwishlistList';
import TotalAmount from '../common/TotalAmount';
import CardFilters from '../card/CardFilters';
import Button from '../common/Button';
import Input from '../common/Input';

const Wishlist = () => {
  const [loading, setLoading] = useState(true);
  const [editable, setEditable] = useState(true);
  const [cards, setCards] = useState([]);
  const [sortedCards, setSortedCards] = useState([]);
  const [cardsNotFound, setCardsNotFound] = useState([]);
  const [cardName, setCardName] = useState('');
  const [filters, setFilters] = useState({});
  const [showSorted, setShowSorted] = useState(false);
  const [showCards, setShowCards] = useState(true);

  useEffect(() => {
    apiHelper.getCurrentUser().then(currentUser => {
      if (currentUser.data.moxfieldWishlist) {
        setEditable(false);
      }
      loadWishlist();
    });
  }, []);

  useEffect(() => {
    loadWishlist();
  }, [filters]);

  const loadWishlist = () => {
    // The colors and mana value filters travel inside `where`, nested under the
    // card by WishlistController — same shape the Collection endpoint takes.
    const advanced = filters.advanced || {};
    const where = { ...(filters.where || {}) };
    if (advanced.colors) where.colors = advanced.colors;
    if (advanced.colorMode) where.colorMode = advanced.colorMode;
    if (advanced.cmc !== null && advanced.cmc !== undefined) {
      where.cmc = advanced.cmc;
      where.cmcComparator = advanced.cmcComparator;
    }

    apiHelper.getWishlist({ where }).then(wishlist => {
      setCards(wishlist.data.map(wishlist => wishlist.card));
      setSortedCards(
        cardUtils.groupCardsByStore(
          wishlist.data
            .map(wishlist => wishlist.card)
            .filter(card => card.cardPrices.length > 0),
        ),
      );
      setCardName(wishlist.data.map(wishlist => wishlist.card.name).join('\n'));
      setLoading(false);
    });
  };

  const handleChange = e => {
    setCardName(e.target.value.toString());
  };

  const handleChangeFilters = filters => {
    setFilters(filters);
  };

  const saveWishlist = e => {
    setLoading(true);
    if (editable) {
      apiHelper
        .updateWishlist(cardName.split('\n'))
        .then(wishlist => {
          setCards(wishlist.data.wishlist.map(wishlist => wishlist.card));
          setSortedCards(
            cardUtils.groupCardsByStore(
              wishlist.data.wishlist
                .map(wishlist => wishlist.card)
                .filter(card => card.cardPrices.length > 0),
            ),
          );
          setCardsNotFound(wishlist.data.cardsNotFound);
          setLoading(false);
        })
        .catch(error => {
          console.log(error);
          setLoading(false);
        });
    } else {
      apiHelper
        .updateMoxfieldWishlist()
        .then(wishlist => {
          setCards(wishlist.data.wishlist.map(wishlist => wishlist.card));
          setSortedCards(
            cardUtils.groupCardsByStore(
              wishlist.data.wishlist
                .map(wishlist => wishlist.card)
                .filter(card => card.cardPrices.length > 0),
            ),
          );
          setCardsNotFound(wishlist.data.cardsNotFound);
          setLoading(false);
        })
        .catch(error => {
          console.log(error);
          setLoading(false);
        });
    }
  };

  return (
    <div className="flex flex-col gap-y-4">
      <h1 className="text-2xl text-center">
        <i className="fas fa-heart mr-2"></i>
        Wishlist
      </h1>
      <h4>
        Las cartas que estás buscando. Te avisamos por mail cuando alguna baja
        de precio o vuelve a tener stock. Si cargás la URL de una wishlist de
        Moxfield en tu perfil, se sincroniza desde ahí.
      </h4>
      <div className="flex flex-col gap-y-4">
        <Input
          type="textarea"
          name="card_name"
          placeholder={
            `El formato debe ser "[cantidad de unidades] [nombre de carta]" (Formato MTGO) o bien "[nombre de carta]"` +
            `\n` +
            `\nEjemplo de formato 1:` +
            `\n1 Return to Nature` +
            `\n1 Safewright Quest` +
            `\n` +
            `\nEjemplo de formato 2:` +
            `\nWhispers of the Muse` +
            `\nIron Myr`
          }
          value={cardName}
          onChange={handleChange}
          disabled={!editable}
        />
        {!loading && cardsNotFound.length > 0 && (
          <CardsNotFoundList cardsNotFound={cardsNotFound} />
        )}

        <CardFilters
          loadData={handleChangeFilters}
          fields={[
            'condition',
            'priceRank',
            'language',
            'price',
            'types',
            'stores',
            'colors',
            'cmc',
          ]}
        />

        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            className="w-full sm:w-1/4"
            iconClass="fas fa-id-card"
            onClick={() => {
              setShowSorted(false);
              setShowCards(true);
            }}
            disabled={loading}>
            Ver por carta
          </Button>
          <Button
            className="w-full sm:w-1/4"
            iconClass="fas fa-store"
            onClick={() => {
              setShowSorted(true);
              setShowCards(true);
            }}
            disabled={loading}>
            Ver por tienda (cartas)
          </Button>
          <Button
            className="w-full sm:w-1/4"
            iconClass="fas fa-list-alt"
            onClick={() => {
              setShowSorted(true);
              setShowCards(false);
            }}
            disabled={loading}>
            Ver por tienda (lista)
          </Button>
          <Button
            className="w-full sm:w-1/4"
            iconClass={editable ? 'fas fa-save' : 'fas fa-download'}
            onClick={() => {
              saveWishlist();
            }}
            disabled={loading}>
            {editable
              ? 'Actualizar wishlist'
              : 'Actualizar wishlist desde Moxfield'}
          </Button>
        </div>

        {loading && <Loader />}
        {!loading && !showSorted && (
          <>
            <TotalAmount cards={cards} />
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-4">
              {cards.map(card => (
                <CardWishlistItem key={card.id} card={card} />
              ))}
            </div>
          </>
        )}
        {!loading && showSorted && (
          <>
            <TotalAmount cards={cards} />
            {sortedCards.map(cardsByStore => (
              <CardWishlistList
                key={cardsByStore.store}
                store={cardsByStore.store}
                cards={cardsByStore.cards}
                showCards={showCards}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default Wishlist;
